import { AlertTriangle, ExternalLink, ShieldAlert, X } from "lucide-react";
import { formatDateTime, formatDistance } from "../format";
import type { DataSource, EvaluatedToilet, HazardEvent } from "../types";
import { useModalDialog } from "../useModalDialog";
import { StatusBadge } from "./StatusBadge";

export function HazardPanel({
  hazards,
  sources,
  alternatives,
  onClose,
  onSelectAlternative,
  originReady = true,
}: {
  hazards: HazardEvent[];
  sources: DataSource[];
  alternatives: EvaluatedToilet[];
  onClose: () => void;
  onSelectAlternative: (item: EvaluatedToilet) => void;
  originReady?: boolean;
}) {
  const sourceMap = new Map(sources.map((source) => [source.id, source]));
  const dialogRef = useModalDialog<HTMLElement>(onClose);
  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="hazard-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hazard-title"
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <div>
            <p className="dialog-step">
              <AlertTriangle aria-hidden="true" />
              現在発生中の情報
            </p>
            <h2 id="hazard-title">トイレ利用への影響</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            data-dialog-initial-focus
          >
            <X aria-hidden="true" />
            <span className="sr-only">ハザード詳細を閉じる</span>
          </button>
        </div>

        {hazards.map((hazard) => {
          const source = sourceMap.get(hazard.sourceId);
          return (
            <article className="hazard-card" key={hazard.id}>
              {hazard.isSimulation && (
                <strong className="simulation-label">
                  訓練・シミュレーション / 実際の災害情報ではありません
                </strong>
              )}
              <h3>{hazard.title}</h3>
              <p className="hazard-summary">{hazard.description}</p>
              <dl>
                <div className="hazard-primary-fact">
                  <dt>影響する時間</dt>
                  <dd>
                    {formatDateTime(hazard.validFrom)}〜{formatDateTime(hazard.validTo)}
                  </dd>
                </div>
                <div className="hazard-primary-fact">
                  <dt>影響する候補</dt>
                  <dd>{hazard.affectedToiletIds.length}施設</dd>
                </div>
                <div className="hazard-secondary-fact">
                  <dt>公式・元データ更新</dt>
                  <dd>{formatDateTime(hazard.sourceUpdatedAt)}</dd>
                </div>
                <div className="hazard-secondary-fact">
                  <dt>情報の限界</dt>
                  <dd>{hazard.limitation}</dd>
                </div>
              </dl>
              {source && (
                <a href={source.sourceUrl} target="_blank" rel="noopener noreferrer">
                  出典：{source.publisher}
                  <ExternalLink aria-hidden="true" />
                  <span className="external-link-note">外部サイト・新しいタブ</span>
                </a>
              )}
            </article>
          );
        })}

        <section className="alternative-list">
          <h3>
            <ShieldAlert aria-hidden="true" />
            影響区域外を優先した候補
          </h3>
          {alternatives.slice(0, 3).map((item) => (
            <button type="button" key={item.id} onClick={() => onSelectAlternative(item)}>
              <StatusBadge state={item.state} compact />
              <span>
                <b>{item.name}</b>
                <small>
                  {originReady
                    ? `徒歩約${item.walkingMinutes}分`
                    : `エリア中心から${formatDistance(item.distanceMeters)}`}
                  ・{item.reasons[0]}
                </small>
              </span>
            </button>
          ))}
        </section>
      </section>
    </div>
  );
}
